import { captureException } from "@sentry/browser";

import { getCustomerInfo } from "../../theme/funct/domainUtils";

/**
 * GA4 Event names and settings.
 * @type {object}
 */
const TRACKING_CONFIG = {
  EVENT_NAME: "high_value_customer_by_email",
  EMAIL_SELECTOR:
    'input[type="email"], input[name="email" i], input[name="stock_request_email"]',
  FORM_ID_DEFAULT: "unknown_id",
};

/**
 * Tracked emails cache to prevent duplicate events.
 * @type {Set<string>}
 */
//const trackedEmails = new Set();
const trackedEmailFormCombinations = new Set(); // New way

/**
 * Gets the form ID from an input element.
 * @param {HTMLInputElement} inputElement - Email input element.
 * @returns {string} Form ID or default value if not found.
 */
function getFormId(inputElement) {
  const form = inputElement.closest("form");
  return form && form.id ? form.id : TRACKING_CONFIG.FORM_ID_DEFAULT;
}

/**
 * Push a high-value customer event to GA4.
 * @param {object} customerInfo - Customer domain information.
 * @param {string} email - Full email address.
 * @param {HTMLInputElement} inputElement - Email input element.
 * @returns {void}
 */
function pushCustomerTypeEvent(customerInfo, email, inputElement) {
  const formId = getFormId(inputElement);
  const trackingKey = `${email}|${formId}`;

  try {
    if (trackedEmailFormCombinations.has(trackingKey)) {
      return;
    }

    const eventItems = {
      event: TRACKING_CONFIG.EVENT_NAME,
      customer_type: customerInfo.customerType,
      form_id: formId,
      customer_email_domain: customerInfo.domain,
    };
    //console.log(eventItems);
    window.dataLayer.push(eventItems);

    trackedEmailFormCombinations.add(trackingKey);
  } catch (error) {
    captureException(error, (scope) => {
      scope.setTag("ga4_event_processing", "high_value_customer_by_email");
      scope.setExtra(
        "context_message",
        "Error during GA4 dataLayer.push for high_value_customer_by_email",
      );
      scope.setExtra("form_id", formId);
      return scope;
    });
  }
}

/**
 * Handle email input changes.
 * @param {Event} event - Input event.
 * @returns {void}
 */
function handleEmailInput(event) {
  const target = /** @type {HTMLInputElement} */ (event.target);
  if (!(event.target instanceof HTMLInputElement)) return;

  const email = target.value;
  const customerInfo = getCustomerInfo(email);

  if (customerInfo && customerInfo.isHighValue) {
    pushCustomerTypeEvent(customerInfo, email, event.target);
  }
}

/**
 * Initializes email field tracking across the site.
 * @returns {void}
 */
export function initializeEmailTracking() {
  const emailFields = document.querySelectorAll(TRACKING_CONFIG.EMAIL_SELECTOR);

  emailFields.forEach((field) => {
    field.addEventListener("blur", handleEmailInput);
  });
}
// noinspection JSUnusedGlobalSymbols
/**
 * Resets tracked emails cache.
 * @returns {void}
 */
export function resetTrackedEmails() {
  trackedEmailFormCombinations.clear();
}

// Export for testing
export { TRACKING_CONFIG, getFormId };
